import { cloneDeep } from 'lodash';
import { compressToBase64 } from 'lz-string';
import { useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import config from 'src/shared/config';
import { T, toGlobalID, toLocalGuid } from 'src/shared/helpers';
import { showErrorNotification } from '../../notificationToaster';
import { loadProductWithProductQuery$data } from '../loaders/__generated__/loadProductWithProductQuery.graphql';
import usePreview3DStore from '../store';
import { tryOnGraphqlMap } from '../tryOnTypes';
import {
    useSaveProductCreateOrUpdateSceneMutation,
    useSaveProductCreateOrUpdateSceneMutation$data
} from './__generated__/useSaveProductCreateOrUpdateSceneMutation.graphql';
import { useSaveProductOptimizedSceneMutation, useSaveProductOptimizedSceneMutation$data } from './__generated__/useSaveProductOptimizedSceneMutation.graphql';
import { TryOnType, useSaveProductCreateOrUpdateVTOMutation } from './__generated__/useSaveProductCreateOrUpdateVTOMutation.graphql';
import { useSaveProductDisableVTOMutation } from './__generated__/useSaveProductDisableVTOMutation.graphql';
import {
    ModelColorCreateInput,
    ModelUpdateOrCreateInput,
    useSaveProductUpdateProductMutation
} from './__generated__/useSaveProductUpdateProductMutation.graphql';
import { useSaveProductUpdateProductOnlyMutation } from './__generated__/useSaveProductUpdateProductOnlyMutation.graphql';

// ✅ NUOVO: Funzione per validare le misurazioni AR
const validateARMeasurements = async (sceneEditor: any): Promise<boolean> => {
    try {
        // Verifica se esiste modelMeasurements con valori validi
        if (typeof sceneEditor.modelMeasurements !== 'undefined' && sceneEditor.modelMeasurements !== null) {
            const measurements = sceneEditor.modelMeasurements;
            const hasValidModelMeasurements =
                measurements &&
                (measurements.x > 0 || measurements.y > 0 || measurements.z > 0 || measurements.width > 0 || measurements.height > 0 || measurements.depth > 0);

            if (hasValidModelMeasurements) {
                return true;
            }
        }

        // Controlla le misurazioni specifiche per ogni mesh
        const meshes = sceneEditor.getSceneMeshes?.() || [];

        for (const mesh of meshes) {
            try {
                const meshId = mesh.id;

                // Controlla tutte le misurazioni specifiche come nel dialog AR
                const width = sceneEditor.getMeshWidthCm?.(meshId);
                const height = sceneEditor.getMeshHeightCm?.(meshId);
                const depth = sceneEditor.getMeshDepthCm?.(meshId);
                const generalSize = sceneEditor.getMeshSizeCm?.(meshId);

                // Se almeno una misurazione è valida (> 0), considera la mesh misurata
                if ((width && width > 0) || (height && height > 0) || (depth && depth > 0) || (generalSize && generalSize > 0)) {
                    return true;
                }
            } catch (error) {
                // Continua con la prossima mesh se c'è un errore
                continue;
            }
        }

        return false;
    } catch (error) {
        // In caso di errore nella validazione, permetti il salvataggio
        return true;
    }
};

// ✅ NUOVO: Funzione esportata per validare AR prima del salvataggio (usata dal SceneEditor)
export const validateARBeforeSave = async (sceneEditor: any): Promise<void> => {
    if (!sceneEditor) return;

    // ✅ PRIMA controlla se l'AR è globalmente abilitato nell'AR Editor
    let isGlobalAREnabled = false;
    try {
        const sceneConfig = sceneEditor.getSceneConfig();
        const generalConfig = sceneConfig?.get?.('general');
        if (generalConfig) {
            isGlobalAREnabled = generalConfig.get('arEnabled') || false;
        }
    } catch (error) {
        console.log('⚠️ AR global config not found, checking mesh-level AR...');
    }

    // Se l'AR globale non è abilitato, salta la validazione
    if (!isGlobalAREnabled) {
        console.log('ℹ️ Global AR disabled, skipping AR validation');
        return;
    }

    console.log('✅ Global AR enabled, proceeding with AR validation...');

    // Controlla se ci sono mesh con AR abilitato
    let hasAREnabled = false;
    try {
        const meshes = sceneEditor.getSceneMeshes?.() || [];

        for (const mesh of meshes) {
            try {
                const arEnabled = sceneEditor.isAREnabledForMesh?.(mesh.id) === true;
                if (arEnabled) {
                    hasAREnabled = true;
                    break;
                }
            } catch (error) {
                continue;
            }
        }
    } catch (error) {
        // In caso di errore, permetti il salvataggio
        return;
    }

    if (hasAREnabled) {
        // Controlla se ci sono misurazioni AR valide
        const hasValidARMeasurements = await validateARMeasurements(sceneEditor);

        if (!hasValidARMeasurements) {
            showErrorNotification({ message: T._('Error while saving: the AR is enabled yet there are no measurements added', 'Admin') });
            throw new Error('AR validation failed: no measurements found');
        }
    }
};

// Shared function to serialize camera-side mappings for both useSaveProduct and useSaveProductAndScene
const serializeCameraSideMappings = (colors: NonNullable<loadProductWithProductQuery$data['model']>['colors']) => {
    const mappings: { colorID: number; sideID: number; cameraLocationID: string | null }[] = [];

    for (const color of colors) {
        for (const side of color.sides) {
            // Only include mappings where cameraLocationID is set (not empty)
            const cameraLocationID = side.cameraLocationID;
            mappings.push({
                colorID: color.colorID!,
                sideID: side.sideID!,
                cameraLocationID: cameraLocationID
            });
        }
    }

    return mappings;
};

// Save just product
const useSaveProduct = () => {
    const [zkProduct, incrementSaveVersion] = usePreview3DStore((store) => [store.zkProduct, store.incrementSaveVersion]);

    /* save product */
    const [updateProduct, isSavingProduct] = useMutation<useSaveProductUpdateProductOnlyMutation>(graphql`
        mutation useSaveProductUpdateProductOnlyMutation($input: ModelUpdateOrCreateInput, $compressedInput: String) {
            createOrUpdateModelV2(input: $input, compressedInput: $compressedInput) {
                model {
                    id
                    salesChannelID
                    salesChannelID
                }
            }
        }
    `);
    const projectMeshes = (meshSide: NonNullable<loadProductWithProductQuery$data['model']>['scenes'][0]['meshSides'][0]) => ({
        sideID: meshSide.sideID,
        colorID: meshSide.colorID,
        meshID: meshSide.meshID,
        matrix: meshSide.matrix,
        transform: meshSide.transform,
        materialID: meshSide.materialID,
        isNormalmapEnabled: false,
        percHeight: 1,
        percWidth: 1,
        subMeshID: 0,
        uvChannel: 0,
        config: {
            general: {
                showDesign: true,
                desktopTextureResolutionWidth: meshSide.config?.general?.desktopTextureResolutionWidth ?? null,
                desktopTextureResolutionHeight: meshSide.config?.general?.desktopTextureResolutionHeight ?? null,
                mobileTextureResolutionWidth: meshSide.config?.general?.mobileTextureResolutionWidth ?? null,
                mobileTextureResolutionHeight: meshSide.config?.general?.mobileTextureResolutionHeight ?? null
            },
            diffuse: {
                enabled: meshSide.config?.diffuse?.enabled ?? true,
                blendMode: meshSide.config?.diffuse?.blendMode ?? 'normal',
                level: meshSide.config?.diffuse?.level ?? 1
            }
        }
    });

    const buildScenePayload = (scene: NonNullable<loadProductWithProductQuery$data['model']>['scenes'][0]) => ({
        sceneID: toLocalGuid(scene.sceneID!),
        isDefault: scene.isDefault,
        meshDesignEffects: [],
        meshSides: scene.meshSides.map(projectMeshes)
    });

    const serializeColors = (colors: NonNullable<loadProductWithProductQuery$data['model']>['colors']): ModelColorCreateInput[] =>
        colors.map((color) => ({
            colorID: color?.colorID ?? null,
            colorGuid: color?.colorGuid ?? '',
            name: color?.name ?? null,
            code: color?.code ?? '',
            colorProviderCode: color?.colorProviderCode ?? null,
            position: color?.position ?? 0,
            sides:
                color?.sides?.map((side) => ({
                    sideID: side?.sideID ?? null,
                    sideGuid: side?.sideGuid ?? null,
                    name: side?.name ?? null,
                    code: side?.code ?? null,
                    imageUrl: side?.imageUrl ?? null,
                    tempImageFileKey: side?.tempImageFileKey ?? '',
                    sortOrder: side?.sortOrder ?? 0,
                    mandatory: side?.mandatory ?? false,
                    canRotate: side?.canRotate ?? false,
                    ppcm: side?.ppcm ?? 0,
                    cameraLocationID: side?.cameraLocationID ?? null,
                    areas:
                        side?.areas?.map((area) => ({
                            areaID: area?.areaID ?? null,
                            guid: area?.guid ?? null,
                            name: area?.name ?? null,
                            path: area?.path ?? null,
                            pathSVG: area?.pathSVG ?? null,
                            boundsX: area?.boundsX ?? 0,
                            boundsY: area?.boundsY ?? 0,
                            boundsWidth: area?.boundsWidth ?? 0,
                            boundsHeight: area?.boundsHeight ?? 0,
                            clipOut: area?.clipOut ?? false,
                            printTypeID: null,
                            layers: [],
                            templateFile: null
                        })) ?? []
                })) ?? []
        }));

    const mapPreviewVariants = (preview: NonNullable<loadProductWithProductQuery$data['model']>['preview3D']) =>
        preview && preview.variantsAttributeOptions
            ? {
                  variantsAttributeOptions: {
                      attributeGuid: preview.variantsAttributeOptions.attributeGuid,
                      variableMaterials:
                          preview.variantsAttributeOptions.variableMaterials?.map((vm) => ({
                              materialID: vm?.materialID,
                              meshes: vm?.meshes
                          })) ?? [],
                      variantOptions:
                          preview.variantsAttributeOptions.variantOptions?.map((vo) => ({
                              variantID: vo?.variantID,
                              optionGuid: vo?.optionGuid,
                              meshes: vo?.meshes,
                              materials:
                                  vo?.materials?.map((mat) => ({
                                      optionMaterialID: mat?.optionMaterialID,
                                      variableMaterialID: mat?.variableMaterialID,
                                      actions:
                                          mat?.actions?.map((action) => ({
                                              actionGuid: action?.actionGuid,
                                              meshID: action?.meshID
                                          })) ?? []
                                  })) ?? []
                          })) ?? []
                  }
              }
            : null;
    const serializeAttributesStep = (steps: NonNullable<loadProductWithProductQuery$data['model']>['attributeSteps']) =>
        steps.map((step) => ({
            cameraLocationID: step.cameraLocationID ?? null,
            displayOrder: step.displayOrder ?? 0,
            groupGuid: step.groupGuid,
            groupID: step.groupID ?? null,
            name: step.name ?? null,
            stepGuid: step.stepGuid,
            stepID: step.stepID ?? null
        }));

    const serializeAttributesGroup = (groups: NonNullable<loadProductWithProductQuery$data['model']>['attributeGroups']) =>
        groups.map((group) => ({
            attributesAlwaysOpened: group.attributesAlwaysOpened ?? false,
            direction: group.direction ?? 0,
            displayOrder: group.displayOrder ?? 0,
            groupGuid: group.groupGuid,
            groupID: group.groupID ?? null,
            imageUrl: group.imageUrl ?? null,
            meshSelector: group.meshSelector ?? null,
            name: group.name ?? null,
            sceneCameraLocationID: group.sceneCameraLocationID ?? null,
            tempImageFileKey: group.tempImageFileKey ?? null,
            tempImageUrl: group.tempImageUrl ?? null
        }));

    const serializeAttributes = (attributes: NonNullable<loadProductWithProductQuery$data['model']>['attributes']) =>
        attributes.map((attribute) => ({
            attributeID: attribute?.attributeID ?? null,
            attributeGuid: attribute?.attributeGuid,
            attributeCode: attribute?.attributeCode ?? null,
            attributeTypeID: attribute?.attributeTypeID ?? 0,
            defaultValue: attribute?.defaultValue ?? null,
            displayOrder: attribute?.displayOrder ?? 0,
            hideOptionsLabel: false,
            preActions: [],
            postActions: [],
            pricings: [],
            valueActions: [],
            name: attribute?.name ?? null,
            description: attribute?.description ?? null,
            groupID: attribute?.groupID ?? null,
            groupGuid: attribute?.groupGuid ?? null,
            stepID: attribute?.stepID ?? null,
            stepGuid: attribute?.stepGuid ?? null,
            optionPreviewShapeID: attribute?.optionPreviewShapeID ?? null,
            options:
                attribute?.options?.map((option) => ({
                    optionID: option?.optionID ?? null,
                    optionGuid: option!.optionGuid,
                    optionCode: option!.optionCode ?? null,
                    tempImageFileKey: option!.tempImageFileKey ?? null,
                    imageUrl: option!.imageUrl ?? null,
                    name: option!.name ?? null,
                    description: option!.description ?? null,
                    displayOrder: option!.displayOrder ?? 0,
                    isDefault: option!.isDefault ?? false,
                    tempImageFileObjectID: option!.tempImageFileObjectID ?? null,
                    imageFileObjectID: option!.imageFileObjectID ?? null,
                    extensionFields: [],
                    actions:
                        option!.actions?.map((action) => ({
                            actionID: action?.actionID ?? null,
                            actionGuid: action!.actionGuid,
                            name: action!.name ?? null,
                            actionTypeID: action!.actionTypeID ?? 0,
                            sceneElementTypeID: action!.sceneElementTypeID ?? 0,
                            selector: action!.selector ?? null,
                            propertyName: action!.propertyName ?? null,
                            value: action!.value ?? null,
                            execOrder: action!.execOrder ?? 0,
                            deviceTypes: action!.deviceTypes ?? []
                        })) ?? []
                })) ?? []
        }));

    const saveProductOnly = async (overrides: { productInfo?: { name: string; code: string; environment: number } } = {}) => {
        const snapshot = zkProduct?.toJSON() as NonNullable<loadProductWithProductQuery$data['model']> | undefined;
        if (!snapshot) {
            throw new Error('Missing product data');
        }

        const freshProduct = !snapshot.modelID || snapshot.modelID === 0;
        const gqlId = freshProduct ? null : toGlobalID(snapshot.modelID, 'Product');

        const attributes = serializeAttributes(snapshot.attributes ?? []);
        const payload: ModelUpdateOrCreateInput = {
            id: gqlId,
            name: { value: overrides.productInfo?.name ?? zkProduct?.get('name') ?? 'New Product' },
            code: { value: overrides.productInfo?.code ?? zkProduct?.get('code') ?? `PROD_${Date.now()}` },
            environment: overrides.productInfo?.environment ? { value: overrides.productInfo.environment } : undefined,
            pricingModelID: freshProduct ? { value: 1 } : undefined,
            salesChannelID: {
                value: freshProduct ? toGlobalID('1', 'SalesChannel') : snapshot.salesChannelID
            },
            scenes: { value: snapshot.scenes.map(buildScenePayload) },
            preview3D: { value: mapPreviewVariants(snapshot.preview3D) },
            colors: { value: serializeColors((snapshot.colors ?? []).filter((c) => !!c?.colorGuid)) },
            attributes: {
                value: attributes
            },
            attributeSteps: { value: serializeAttributesStep(snapshot.attributeSteps ?? []) },
            attributeGroups: { value: serializeAttributesGroup(snapshot.attributeGroups ?? []) },
            cameraSideMappings: { value: serializeCameraSideMappings(snapshot.colors ?? []) }
        };

        const serializedInput = JSON.stringify(payload);
        const compressed = compressToBase64(serializedInput);

        await new Promise<void>((resolve) => {
            updateProduct({
                variables: config.enableCompressionForSavingReadingProducts ? { compressedInput: compressed } : { input: payload },
                onCompleted: () => resolve()
            });
        });

        // incrementSaveVersion();
    };

    return [saveProductOnly, isSavingProduct] as const;
};

// Save the scene with visibility metadata optimization (used by the composer flow).
// Snapshots currently visible mesh IDs into scene.metadata.visibleMeshesID, hides every
// mesh for a clean "all hidden" .babylon baseline, exports the scene, then restores the
// live view so the canvas doesn't appear blank.
const useSaveOptimizedScene = () => {
    const [zkProduct, zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkProduct, store.zkScene, store.sceneEditor]);
    const [isExportingScene, setIsExportingScene] = useState(false);

    const [createOrUpdateScene, isSavingScene] = useMutation<useSaveProductOptimizedSceneMutation>(graphql`
        mutation useSaveProductOptimizedSceneMutation($input: SceneInput!) {
            updateScene(input: $input) {
                scene {
                    id
                }
            }
        }
    `);

    const saveOptimizedScene = async (onProgress?: (progress: number) => void) => {
        const { EditingMode } = await import('@zakeke/zakeke-3d-core');

        sceneEditor?.setEditingMode(EditingMode.None);
        sceneEditor!.showHideEditControl(false);

        (sceneEditor as any)?.saveVisibleMeshesToMetadata?.();

        setIsExportingScene(true);
        await new Promise<void>((resolve, reject) => {
            sceneEditor?.updateSceneModel(
                () => resolve(),
                () => reject(),
                onProgress
            );
        });
        setIsExportingScene(false);

        (sceneEditor as any)?.applyVisibleMeshesFromMetadata?.();

        const sceneJson = cloneDeep(sceneEditor!.zkScene!.toJSON());

        if (sceneJson.config?.general?.tryOnSettings?.type !== undefined) {
            const originalType = sceneJson.config.general.tryOnSettings.type;
            const convertedType = tryOnGraphqlMap.get(originalType);
            if (convertedType) {
                sceneJson.config.general.tryOnSettings.type = convertedType;
            } else {
                delete sceneJson.config.general.tryOnSettings;
            }
        }

        const saveSceneResult = await new Promise<useSaveProductOptimizedSceneMutation$data>((resolve, reject) => {
            createOrUpdateScene({
                variables: {
                    input: {
                        ...sceneJson,
                        id: toGlobalID(zkScene!.get('sceneID') || zkScene!.get('tempSceneID'), 'Scene'),
                        tempSceneID: undefined,
                        sceneID: undefined,
                        sceneIDforPagination: undefined
                    }
                },
                onCompleted: (res) => resolve(res),
                onError: () => reject()
            });
        });

        const savedSceneID = toLocalGuid(saveSceneResult.updateScene.scene!.id)!;
        zkScene!.set('sceneID', savedSceneID);
        zkProduct!.get('scenes').at(0).set('sceneID', savedSceneID);
    };

    const isSaving = isExportingScene || isSavingScene;

    return [saveOptimizedScene, isSaving] as const;
};

// save product and scene
const useSaveProductAndScene = () => {
    const [zkProduct, zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkProduct, store.zkScene, store.sceneEditor]);
    const [isExportingScene, setIsExportingScene] = useState(false);

    /* Save product */
    const [updateProduct, isSavingProduct] = useMutation<useSaveProductUpdateProductMutation>(graphql`
        mutation useSaveProductUpdateProductMutation($input: ModelUpdateOrCreateInput, $compressedInput: String) {
            createOrUpdateModelV2(input: $input, compressedInput: $compressedInput) {
                model {
                    id
                    salesChannelID
                }
            }
        }
    `);

    /* Save scene */
    const [createOrUpdateScene, isSavingScene] = useMutation<useSaveProductCreateOrUpdateSceneMutation>(graphql`
        mutation useSaveProductCreateOrUpdateSceneMutation($input: SceneInput!) {
            updateScene(input: $input) {
                scene {
                    id
                }
            }
        }
    `);

    /* Save VTO */
    const [createOrUpdateVTO] = useMutation<useSaveProductCreateOrUpdateVTOMutation>(graphql`
        mutation useSaveProductCreateOrUpdateVTOMutation($input: ProductTryOnInput!) {
            updateOrCreateTryOnProduct(input: $input) {
                productTryOn {
                    id
                }
            }
        }
    `);

    /* Disable VTO */
    const [disableProduct, isDisablingVTO] = useMutation<useSaveProductDisableVTOMutation>(graphql`
        mutation useSaveProductDisableVTOMutation($id: ID!) {
            disableTryOnProduct(id: $id) {
                productTryOn {
                    id
                }
            }
        }
    `);

    const saveProduct = async (
        settings: {
            onProgress?: (progress: number) => void;
            fromSceneEditor?: boolean;
            productInfo?: { name: string; code: string; environment: number };
        } = {}
    ) => {
        const { EditingMode } = await import('@zakeke/zakeke-3d-core');

        // Disable the edit mode to avoid axis to be saved with the scene.
        sceneEditor?.setEditingMode(EditingMode.None);
        sceneEditor!.showHideEditControl(false);

        setIsExportingScene(true);
        await new Promise<void>((resolve, reject) => {
            sceneEditor?.updateSceneModel(
                () => resolve(),
                () => reject(),
                settings.onProgress
            );
        });
        setIsExportingScene(false);

        const sceneJson = cloneDeep(sceneEditor!.zkScene!.toJSON());

        // Convert number to graphql type
        if (sceneJson.config?.general?.tryOnSettings?.type !== undefined) {
            const originalType = sceneJson.config.general.tryOnSettings.type;
            const convertedType = tryOnGraphqlMap.get(originalType);
            if (convertedType) {
                sceneJson.config.general.tryOnSettings.type = convertedType;
            } else {
                console.warn('Failed to convert VTO type, removing tryOnSettings from scene');
                // Se non riusciamo a convertire il tipo, rimuoviamo le impostazioni VTO dalla scena
                delete sceneJson.config.general.tryOnSettings;
            }
        }

        const saveSceneResult = await new Promise<useSaveProductCreateOrUpdateSceneMutation$data>((resolve, reject) => {
            createOrUpdateScene({
                variables: {
                    input: {
                        ...sceneJson,
                        id: toGlobalID(zkScene!.get('sceneID') || zkScene!.get('tempSceneID'), 'Scene'),
                        tempSceneID: undefined, // use graphql id
                        sceneID: undefined, // use graphql id
                        sceneIDforPagination: undefined // use graphql id
                    }
                },
                onCompleted: (res) => {
                    resolve(res);
                },
                onError: () => {
                    reject();
                }
            });
        });

        zkScene!.set('sceneID', toLocalGuid(saveSceneResult.updateScene.scene!.id)!);
        zkProduct!.get('scenes').at(0).set('sceneID', toLocalGuid(saveSceneResult.updateScene.scene!.id)!);

        const product = zkProduct!.toJSON() as NonNullable<loadProductWithProductQuery$data['model']>;

        // ✅ NUOVO: Determina se è un nuovo prodotto
        const isNewProduct = !product.modelID || product.modelID === 0;
        const productId = isNewProduct ? null : toGlobalID(product.modelID, 'Product');

        console.log('🔍 Save Product Debug Info:', {
            isNewProduct,
            originalModelID: product.modelID,
            productId,
            hasScenes: product.scenes?.length > 0,
            sceneID: product.scenes?.[0]?.sceneID,
            productName: settings.productInfo?.name || zkProduct!.get('name'),
            productCode: settings.productInfo?.code || zkProduct!.get('code'),
            environment: settings.productInfo?.environment || zkProduct!.get('environment'), // ✅ Usa productInfo se disponibile
            salesChannelID: product.salesChannelID
        });

        const mutationInputForUpdateProduct: ModelUpdateOrCreateInput = {
            id: productId,
            name: { value: settings.productInfo?.name || zkProduct!.get('name') || 'New Product' },
            code: { value: settings.productInfo?.code || zkProduct!.get('code') || `PROD_${Date.now()}` },
            environment: settings.productInfo?.environment ? { value: settings.productInfo.environment } : undefined,
            pricingModelID: isNewProduct ? { value: 1 } : undefined,
            salesChannelID: {
                value: isNewProduct ? toGlobalID((1).toString(), 'SalesChannel') : product.salesChannelID
            },
            scenes: {
                value: product.scenes.map((scene) => {
                    return {
                        sceneID: toLocalGuid(scene!.sceneID!),
                        isDefault: scene!.isDefault,
                        meshDesignEffects: [],
                        meshSides: scene!.meshSides.map((meshSide) => {
                            return {
                                sideID: meshSide.sideID,
                                colorID: meshSide.colorID,
                                config: {
                                    general: {
                                        desktopTextureResolutionHeight: meshSide.config?.general?.desktopTextureResolutionHeight ?? null,
                                        desktopTextureResolutionWidth: meshSide.config?.general?.desktopTextureResolutionWidth ?? null,
                                        mobileTextureResolutionHeight: meshSide.config?.general?.mobileTextureResolutionHeight ?? null,
                                        mobileTextureResolutionWidth: meshSide.config?.general?.mobileTextureResolutionWidth ?? null,
                                        showDesign: true
                                    },
                                    diffuse: {
                                        enabled: meshSide.config?.diffuse?.enabled ?? true,
                                        blendMode: meshSide.config?.diffuse?.blendMode ?? 'normal',
                                        level: meshSide.config?.diffuse?.level ?? 1
                                    }
                                },
                                meshID: meshSide.meshID,
                                matrix: meshSide.matrix,
                                transform: meshSide.transform,
                                isNormalmapEnabled: false,
                                percHeight: 1,
                                percWidth: 1,
                                subMeshID: 0,
                                uvChannel: 0,
                                materialID: meshSide.materialID
                            };
                        })
                    };
                })
            },
            preview3D: {
                value: product.preview3D
                    ? {
                          variantsAttributeOptions: {
                              attributeGuid: product.preview3D.variantsAttributeOptions!.attributeGuid,
                              variableMaterials: product.preview3D.variantsAttributeOptions!.variableMaterials!.map((variableMaterial) => {
                                  return {
                                      materialID: variableMaterial!.materialID,
                                      meshes: variableMaterial!.meshes
                                  };
                              }),
                              variantOptions: product.preview3D.variantsAttributeOptions!.variantOptions!.map((variantOption) => {
                                  return {
                                      variantID: variantOption!.variantID,
                                      optionGuid: variantOption!.optionGuid,
                                      meshes: variantOption!.meshes,
                                      materials: variantOption!.materials!.map((material) => {
                                          return {
                                              optionMaterialID: material!.optionMaterialID,
                                              variableMaterialID: material!.variableMaterialID,
                                              actions: material!.actions!.map((action) => {
                                                  return {
                                                      actionGuid: action!.actionGuid,
                                                      meshID: action!.meshID
                                                  };
                                              })
                                          };
                                      })
                                  };
                              })
                          }
                      }
                    : null
            },
            attributes: {
                value: product.attributes!.map((attribute) => {
                    return {
                        attributeID: attribute?.attributeID ?? null,
                        attributeGuid: attribute!.attributeGuid,
                        attributeCode: attribute!.attributeCode,
                        attributeTypeID: attribute!.attributeTypeID,
                        defaultValue: attribute!.defaultValue,
                        displayOrder: attribute!.displayOrder,
                        hideOptionsLabel: false,
                        preActions: [],
                        postActions: [],
                        pricings: [],
                        valueActions: [],
                        name: attribute!.name,
                        description: attribute!.description,
                        groupID: attribute!.groupID,
                        groupGuid: attribute!.groupGuid,
                        stepID: attribute!.stepID,
                        stepGuid: attribute!.stepGuid,
                        optionPreviewShapeID: attribute!.optionPreviewShapeID,
                        options: attribute!.options!.map((option) => {
                            return {
                                optionID: option?.optionID ?? null,
                                optionGuid: option!.optionGuid,
                                optionCode: option!.optionCode,
                                tempImageFileKey: option!.tempImageFileKey,
                                imageUrl: option!.imageUrl,
                                name: option!.name,
                                description: option!.description,
                                displayOrder: option!.displayOrder,
                                isDefault: option!.isDefault,
                                tempImageFileObjectID: option!.tempImageFileObjectID,
                                imageFileObjectID: option!.imageFileObjectID,
                                extensionFields: [],
                                actions: option!.actions!.map((action) => {
                                    return {
                                        actionID: action?.actionID ?? null,
                                        actionGuid: action!.actionGuid,
                                        name: action!.name,
                                        actionTypeID: action!.actionTypeID,
                                        sceneElementTypeID: action!.sceneElementTypeID,
                                        selector: action!.selector,
                                        propertyName: action!.propertyName,
                                        value: action!.value,
                                        execOrder: action!.execOrder,
                                        deviceTypes: action!.deviceTypes
                                    };
                                })
                            };
                        })
                    };
                })
            },
            cameraSideMappings: {
                value: serializeCameraSideMappings(product.colors)
            }
        };

        console.log({ product: mutationInputForUpdateProduct });
        console.log('🔍 Final Mutation Input Debug:', {
            hasId: 'id' in mutationInputForUpdateProduct,
            idValue: mutationInputForUpdateProduct.id,
            isNewProduct,
            useCompression: config.enableCompressionForSavingReadingProducts
        });

        // ✅ NUOVO: Log del JSON prima della compressione
        const jsonBeforeCompression = JSON.stringify(mutationInputForUpdateProduct);
        console.log('🔍 JSON Before Compression:', jsonBeforeCompression);
        console.log('🔍 JSON Parsed Back:', JSON.parse(jsonBeforeCompression));

        let compressedMutationInput = compressToBase64(jsonBeforeCompression);

        await new Promise<void>((resolve) => {
            updateProduct({
                variables: config.enableCompressionForSavingReadingProducts
                    ? {
                          compressedInput: compressedMutationInput
                      }
                    : {
                          input: mutationInputForUpdateProduct
                      },
                onCompleted: (res) => {
                    resolve();
                }
            });
        });

        const isTryOnEnabled = zkScene!.getTryOnEnabled() ?? false;
        const tryonSettings = zkScene!.getTryOnSettings(); // They have all the same info of the microservice, so I can use it as a state.

        console.log('🔍 VTO Save Debug:', {
            isTryOnEnabled,
            tryonSettings,
            originalType: tryonSettings?.type,
            originalTypeType: typeof tryonSettings?.type,
            isValidNumber: Number.isInteger(tryonSettings?.type),
            tryOnGraphqlMapEntries: Array.from(tryOnGraphqlMap.entries())
        });

        if (isTryOnEnabled && tryonSettings && !isNewProduct) {
            console.log('Updating VTO configuration...');

            // 🔧 DEBUG: Controlla la conversione del tipo
            const originalType = tryonSettings.type;

            // 🔧 FIX: Controlla se il tipo è NotSelected (0) e impedisci il salvataggio
            if (originalType === 0) {
                // TryOnType.NotSelected
                console.error('❌ Cannot save VTO with NotSelected type:', {
                    originalType,
                    isTryOnEnabled,
                    tryonSettings
                });
                throw new Error('VTO is enabled but no type is selected. Please select a VTO type before saving.');
            }

            let convertedTypeID = tryOnGraphqlMap.get(originalType);

            // 🔧 FIX: Se la conversione fallisce, prova a convertire da stringa a numero
            if (!convertedTypeID && typeof originalType === 'string') {
                console.log('🔄 Trying to parse string type to number:', originalType);
                const parsedType = parseInt(originalType, 10);
                if (!isNaN(parsedType)) {
                    convertedTypeID = tryOnGraphqlMap.get(parsedType);
                    console.log('🔄 Parsed type conversion result:', { parsedType, convertedTypeID });
                }
            }

            // 🔧 FIX: Se ancora fallisce, prova a fare il match diretto con le stringhe GraphQL
            if (!convertedTypeID && typeof originalType === 'string') {
                const directStringMatch = (originalType as string).toUpperCase();
                const possibleValues = Array.from(tryOnGraphqlMap.values());
                if (possibleValues.includes(directStringMatch as any)) {
                    convertedTypeID = directStringMatch as any;
                    console.log('🔄 Direct string match found:', directStringMatch);
                }
            }

            console.log('🔄 VTO Type Conversion:', {
                originalType,
                convertedTypeID,
                isValidConversion: convertedTypeID !== undefined
            });

            if (!convertedTypeID) {
                console.error('❌ Failed to convert VTO type for GraphQL:', {
                    originalType,
                    availableTypes: Array.from(tryOnGraphqlMap.keys()),
                    availableGraphQLValues: Array.from(tryOnGraphqlMap.values()),
                    tryOnGraphqlMap: Object.fromEntries(tryOnGraphqlMap)
                });
                throw new Error(`Invalid VTO type conversion: ${originalType} (type: ${typeof originalType})`);
            }

            await new Promise<void>((resolve, reject) => {
                const vtoInput = {
                    id: productId!, // Non-null assertion perché sappiamo che !isNewProduct
                    productName: zkProduct!.get('name'),
                    productCode: zkProduct!.get('code'),
                    typeID: convertedTypeID as TryOnType,
                    settings: {
                        pd: tryonSettings.typeSettings.pd ?? false,
                        tryOn: tryonSettings.typeSettings.try_on ?? false,
                        active: tryonSettings.typeSettings.active ?? false
                    },
                    // 🔧 CORRETTO: Usa il campo mandatoryPD separato se disponibile, altrimenti fallback a pd per retrocompatibilità
                    mandatoryPD: (tryonSettings.typeSettings as any).mandatoryPD ?? tryonSettings.typeSettings.pd ?? false
                };

                console.log(' VTO TypeSettings Detail:', {
                    typeSettings: tryonSettings.typeSettings,
                    mandatoryPDFromTypeSettings: (tryonSettings.typeSettings as any).mandatoryPD,
                    pdFromTypeSettings: tryonSettings.typeSettings.pd,
                    finalMandatoryPDValue: (tryonSettings.typeSettings as any).mandatoryPD ?? tryonSettings.typeSettings.pd ?? false
                });

                console.log('📤 VTO Mutation Input:', vtoInput);

                createOrUpdateVTO({
                    variables: {
                        input: vtoInput
                    },
                    onCompleted: () => {
                        console.log('VTO configuration updated successfully');
                        resolve();
                    },
                    onError: (error) => {
                        console.error('Error updating VTO configuration:', error);
                        reject();
                    }
                });
            });
        } else if (tryonSettings && !isTryOnEnabled && !isNewProduct) {
            // Solo disabilita se il prodotto aveva precedentemente il VTO configurato E non è un prodotto nuovo
            console.log('Disabling VTO for product that had VTO previously...');
            await new Promise<void>((resolve, reject) => {
                disableProduct({
                    variables: {
                        id: productId! // Non-null assertion perché sappiamo che !isNewProduct
                    },
                    onCompleted: () => {
                        console.log('VTO disabled successfully');
                        resolve();
                    },
                    onError: (error) => {
                        console.error('Error disabling VTO:', error);
                        // Non fare reject per errori di disabilitazione VTO
                        // perché il prodotto potrebbe non avere mai avuto VTO configurato
                        console.log('Continuing save process despite VTO disable error...');
                        resolve();
                    }
                });
            });
        } else {
            console.log('No VTO configuration found or needed, skipping VTO operations');
        }
    };

    const isSaving = isExportingScene || isSavingProduct || isSavingScene || isDisablingVTO;

    return [saveProduct, isSaving] as const;
};

export default useSaveProductAndScene;

export { useSaveProduct, useSaveOptimizedScene };
